import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  AccionesRol,
  EstadoEvento,
  EstadoEventoTecnico,
  IActivo,
  ICreateTratamientoEvento,
  IDispositivoAlarma,
} from 'modelos/src';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { TratamientoEventosService } from '../../../../../auxiliares/servicios/http/tratamientos-eventos.service';
import { ActivosService } from '../../../../../auxiliares/servicios/http/activos.service';
import { AlarmasDomiciliariasService } from '../../../alarmas/alarmas-domiciliarias/alarmas-domiciliarias.service';
import { isModoDesactivadoActivo } from '../../../../../auxiliares/modo-desactivado.util';

export interface IAgregarNotaGenericoData {
  idsEventos: string[];
  estado?: EstadoEvento;
  estadoTecnico?: EstadoEventoTecnico;
  /** Entidad del evento, para ofrecer reactivar el modo desactivado al finalizar */
  activo?: IActivo;
  alarma?: IDispositivoAlarma;
}

@Component({
  selector: 'app-agregar-nota-generico',
  templateUrl: './agregar-nota-generico.component.html',
  styleUrl: './agregar-nota-generico.component.scss',
  standalone: false,
})
export class AgregarNotaGenericoComponent implements OnInit {
  data = inject<IAgregarNotaGenericoData>(MAT_DIALOG_DATA);
  helper = inject(HelperService);
  private service = inject(TratamientoEventosService);
  private serviceActivo = inject(ActivosService);
  private serviceAlarma = inject(AlarmasDomiciliariasService);
  dialogRef = inject<MatDialogRef<AgregarNotaGenericoComponent>>(MatDialogRef);

  public loading: boolean = true;
  public form?: FormGroup;
  public reactivarDispositivo = false;

  /** Permiso de modo desactivado según el tipo de entidad */
  private get permisoModoDesactivado(): AccionesRol {
    if (this.data.alarma) return 'Alarmas - Cambiar modo desactivado';
    switch (this.data.activo?.categoria) {
      case 'Vehículo':
        return 'Vehículos - Cambiar modo desactivado';
      case 'Colectivo':
        return 'Colectivos - Cambiar modo desactivado';
      default:
        return 'Activos - Cambiar modo desactivado';
    }
  }

  public get mostrarReactivar(): boolean {
    if (this.data.estadoTecnico !== 'Finalizado') return false;
    const entidad = this.data.activo ?? this.data.alarma;
    if (!isModoDesactivadoActivo(entidad?.modoDesactivado)) return false;
    return this.helper.tienePermiso(this.permisoModoDesactivado);
  }

  private createForm() {
    this.form = new FormGroup({
      idsEventos: new FormControl(this.data?.idsEventos),
      nota: new FormControl(),
      notaInterna: new FormControl(),
      estado: new FormControl(this.data?.estado),
      estadoTecnico: new FormControl(this.data?.estadoTecnico),
    });
  }

  //
  public async onSubmit() {
    this.loading = true;
    try {
      const data = this.getData();
      await this.service.createGenerico(data);
      this.helper.notifSuccess('Nota agregada correctamente');
      if (this.mostrarReactivar && this.reactivarDispositivo) {
        await this.reactivar();
      }
      this.dialogRef.close(data);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  /** Reactiva el dispositivo tras finalizar; nunca lanza (el tratamiento ya se creó) */
  private async reactivar(): Promise<void> {
    try {
      const update = {
        modoDesactivado: { dispositivoDesactivado: false, permanente: false },
      };
      if (this.data.alarma?._id) {
        await this.serviceAlarma.update(this.data.alarma._id, update);
      } else if (this.data.activo?._id) {
        await this.serviceActivo.update(this.data.activo._id, update);
      }
      this.helper.notifSuccess('Dispositivo reactivado');
    } catch (error) {
      console.error(error);
      this.helper.notifError(
        'El servicio se finalizó pero no se pudo reactivar el dispositivo. Reactívelo manualmente desde el listado.',
      );
    }
  }

  public getData(): ICreateTratamientoEvento {
    const data: ICreateTratamientoEvento = this.form?.value;
    return data;
  }

  async ngOnInit() {
    this.createForm();
    this.loading = false;
  }
}
